// RehearsalApp — the 10-screen God Reference flow (handoff 2026-06-25).
//
// Mounted at /rehearsal. State-machine driven (no nested routes); mirrors
// the prototype's `screen` flag with a React enum. Screens, in order:
//
//   booth → prep → rec → saved → match → matched → select → setup → player → done
//
// All styling is inline to match the prototype tokens verbatim. The friend
// is not the future-Off-Book user — she's the first demo, and the demo
// IS the design. The current 4 editorial screens remain at /library-v2
// etc. for fallback; this app replaces them on the /import deep-link path.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useArc } from "../state/arc";
import {
  ActorScene,
  ActorScenesEnvelope,
  SceneTake,
  SceneManifestEntry,
  MatchStatus,
  getActorScenes,
  postSceneTake,
  postLineRetake,
  getSceneTake,
  startMatch,
  getMatch,
} from "../api/scenes";
import {
  CastEnvelope, CastSynthState,
  getCast, startSynthesize, getSynthStatus, castAudioUrl,
} from "../api/cast";

// ─────────────────────────────────────────────────────────────
// Design tokens — exact values from OffBook.dc.html handoff
// ─────────────────────────────────────────────────────────────

const TOKENS = {
  deepInk: "#20202A",
  mutedPlum: "#6F6178",
  coral: "#F25F57",
  peach: "#F6896F",
  success: "#1F8A5B",
  glass: "rgba(255,255,255,0.22)",
  glassStrong: "rgba(255,255,255,0.36)",
  glassThin: "rgba(255,255,255,0.16)",
  border: "rgba(255,255,255,0.40)",
  font: "-apple-system, 'SF Pro Display', 'SF Pro Text', system-ui, sans-serif",
};

const PRIMARY_GRADIENT = `linear-gradient(180deg, ${TOKENS.peach}, ${TOKENS.coral})`;
const COVER_GRADIENT = "linear-gradient(150deg,#5A4368,#7E4A6B 55%,#B26478)";

// Lavender→blue background with peach/coral/cream blooms (from God Ref).
const BG_STYLE: React.CSSProperties = {
  background: [
    "radial-gradient(96% 60% at 80% 5%, rgba(246,182,158,.78), transparent 55%)",
    "radial-gradient(100% 78% at 8% 103%, rgba(135,175,198,.74), transparent 60%)",
    "radial-gradient(86% 56% at 52% 47%, rgba(242,95,87,.15), transparent 62%)",
    "linear-gradient(180deg,#c7bacb 0%,#aeb1c6 52%,#92b2c5 100%)",
  ].join(","),
};

// ─────────────────────────────────────────────────────────────
// Helper components
// ─────────────────────────────────────────────────────────────

function ScreenShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        height: "100dvh",
        overflow: "hidden",
        color: TOKENS.deepInk,
        fontFamily: TOKENS.font,
        ...BG_STYLE,
      }}
    >
      {/* Drifting bloom overlays (animated by inline @keyframes via the stylesheet) */}
      <div
        style={{
          position: "absolute", top: "9%", left: "50%",
          transform: "translateX(-50%)", width: 250, height: 250, borderRadius: "50%",
          background: "radial-gradient(circle,rgba(246,150,120,.6),transparent 66%)",
          filter: "blur(44px)", animation: "drift 11s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute", bottom: "6%", left: "-6%",
          width: 240, height: 240, borderRadius: "50%",
          background: "radial-gradient(circle,rgba(135,175,198,.55),transparent 66%)",
          filter: "blur(46px)", animation: "drift 14s ease-in-out infinite reverse",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "max(env(safe-area-inset-top), 14px)",
          right: "max(env(safe-area-inset-right), 14px)",
          left: "max(env(safe-area-inset-left), 14px)",
          bottom: "max(env(safe-area-inset-bottom), 14px)",
          display: "flex", flexDirection: "column",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function PrimaryButton({
  onClick, disabled, children, style,
}: { onClick?: () => void; disabled?: boolean; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        appearance: "none", border: "none", cursor: disabled ? "not-allowed" : "pointer",
        width: "100%", padding: 17, borderRadius: 19,
        background: PRIMARY_GRADIENT, color: "#fff",
        font: `600 16.5px ${TOKENS.font}`, letterSpacing: "0.01em",
        boxShadow: "0 12px 26px rgba(242,95,87,.32)",
        opacity: disabled ? 0.55 : 1,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({
  onClick, disabled, children, style,
}: { onClick?: () => void; disabled?: boolean; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        appearance: "none", border: `1px solid ${TOKENS.glassStrong}`, cursor: disabled ? "not-allowed" : "pointer",
        width: "100%", padding: 15, borderRadius: 18,
        background: "rgba(255,255,255,0.32)", color: TOKENS.deepInk,
        font: `500 15px ${TOKENS.font}`,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        opacity: disabled ? 0.55 : 1,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

function GlassCard({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        background: TOKENS.glass,
        backdropFilter: "blur(22px) saturate(150%)",
        WebkitBackdropFilter: "blur(22px) saturate(150%)",
        border: `1px solid ${TOKENS.glassStrong}`,
        boxShadow: "0 10px 26px rgba(32,32,42,0.08)",
        borderRadius: 20,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ font: `600 10.5px ${TOKENS.font}`, letterSpacing: "0.18em", color: TOKENS.mutedPlum, textTransform: "uppercase" }}>
      {children}
    </div>
  );
}

function ScreenTitle({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ font: `300 27px ${TOKENS.font}`, letterSpacing: "-0.01em", color: TOKENS.deepInk, ...style }}>
      {children}
    </div>
  );
}

function CheckIcon({ size = 18, color = "#fff" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

function CircleIcon({ size = 18, fill = TOKENS.success }: { size?: number; fill?: string }) {
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: fill, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <CheckIcon size={size * 0.6} />
    </div>
  );
}

function fmtTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

// ─────────────────────────────────────────────────────────────
// State machine
// ─────────────────────────────────────────────────────────────

type Screen = "loading" | "booth" | "prep" | "rec" | "saved" | "match" | "matched"
            | "review" | "rerecord-line"
            | "select" | "cast-load" | "setup" | "player" | "done";

interface AppState {
  envelope: ActorScenesEnvelope | null;
  takes: Record<number, SceneTake | null>;   // scene_n → take
  matches: Record<number, MatchStatus>;      // scene_n → match status
  matchLoading: Record<number, boolean>;     // scene_n → match still loading?
  currentSceneN: number;
  currentLineId: number | null;              // for /rerecord-line
  recElapsedS: number;
  savedDurMs: number;
  // Lines the user just re-recorded — show as CLEAN even if confidence is stale.
  // Keyed by scene_n → set of line_ids.
  justCleanedByScene: Record<number, Set<number>>;
  // Cast (ElevenLabs partner voices)
  cast: CastEnvelope | null;
  castSynth: CastSynthState | null;
  // playback config
  selScenes: Record<number, boolean>;
  tOthers: boolean;
  tDesc: boolean;
  playSasha: boolean;
  pauseBefore: "off" | "short" | "long";
}

const INITIAL_STATE: AppState = {
  envelope: null,
  takes: {},
  matches: {},
  matchLoading: {},
  currentSceneN: 1,
  currentLineId: null,
  recElapsedS: 0,
  savedDurMs: 0,
  justCleanedByScene: {},
  cast: null,
  castSynth: null,
  selScenes: {},
  // DEFAULTS: just play her recording back. Cast voicing, skip-mine, and
  // pre-line pauses are all OPT-IN per Ryan iPhone test 2026-06-26
  // ("I just wanted to hear myself" / "randomly assigning a cast I didn't
  // pick"). Run Lines now defaults to a straight playback of the take.
  tOthers: false,
  tDesc: false,
  playSasha: true,
  pauseBefore: "off",
};

// LocalStorage cache so a reload shows last-known match state immediately
// while we re-fetch in the background. Per Ryan iPhone test 2026-06-25 —
// she lost the sense that her work persisted between sessions.
const MATCH_CACHE_KEY = (projectId: string) => `offbook.matches.${projectId}`;
function loadCachedMatches(projectId: string): { takes: Record<number, SceneTake | null>; matches: Record<number, MatchStatus> } | null {
  try {
    const raw = localStorage.getItem(MATCH_CACHE_KEY(projectId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}
function saveCachedMatches(projectId: string, takes: Record<number, SceneTake | null>, matches: Record<number, MatchStatus>) {
  try {
    localStorage.setItem(MATCH_CACHE_KEY(projectId), JSON.stringify({ takes, matches }));
  } catch { /* no-op */ }
}

// Fetch with a timeout so a slow Fly cold-start doesn't hang the Booth open.
async function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const t = window.setTimeout(() => resolve(null), ms);
    p.then((v) => { window.clearTimeout(t); resolve(v); }).catch(() => { window.clearTimeout(t); resolve(null); });
  });
}

// Heuristic confidence per manifest entry — matched / expected.
function lineConfidence(m: SceneManifestEntry): number {
  const exp = (m.expected ?? 0);
  const mat = (m.matched ?? 0);
  if (!exp) return 1;
  return Math.max(0, Math.min(1, mat / exp));
}
const FLAG_THRESHOLD = 0.6;

// ─────────────────────────────────────────────────────────────
// Main app
// ─────────────────────────────────────────────────────────────

export default function RehearsalApp() {
  const navigate = useNavigate();
  const { state: arc } = useArc();
  const projectId = arc.projectId;
  const role = arc.selectedCharacter || "SASHA";

  const [screen, setScreen] = useState<Screen>("loading");
  const [app, setApp] = useState<AppState>(INITIAL_STATE);

  // Load actor_scenes + status of each take/match. Two stages so a slow
  // cold-start doesn't freeze the friend on "Opening the studio…":
  //   1. Fetch actor_scenes envelope (~10s timeout) and immediately surface
  //      Booth with cached take/match state from localStorage if any.
  //   2. Re-fetch each scene's take + match in parallel with per-call
  //      5s timeouts. Update Booth state as each comes back. Failed calls
  //      keep the cached value rather than zeroing it.
  useEffect(() => {
    if (!projectId) {
      navigate("/library");
      return;
    }
    let cancelled = false;
    (async () => {
      const cached = loadCachedMatches(projectId);
      const env = await withTimeout(getActorScenes(projectId), 15000);
      if (cancelled) return;
      if (!env) {
        alert("Couldn't reach the backend. Check connection and try again.");
        navigate("/library");
        return;
      }
      // Seed with cached values + mark every scene as "loading"
      const seededTakes: Record<number, SceneTake | null> = {};
      const seededMatches: Record<number, MatchStatus> = {};
      const matchLoading: Record<number, boolean> = {};
      const selScenes: Record<number, boolean> = {};
      for (const sc of env.actor_scenes) {
        seededTakes[sc.n] = cached?.takes?.[sc.n] ?? null;
        seededMatches[sc.n] = cached?.matches?.[sc.n] ?? { status: "not_started", progress: 0 };
        matchLoading[sc.n] = true;
        selScenes[sc.n] = (seededMatches[sc.n]?.status === "complete");
      }
      setApp((s) => ({ ...s, envelope: env, takes: seededTakes, matches: seededMatches, matchLoading, selScenes }));
      setScreen("booth");

      // Background refresh — parallel, each call timeboxed
      await Promise.all(env.actor_scenes.map(async (sc) => {
        const [t, m] = await Promise.all([
          withTimeout(getSceneTake(projectId, sc.n), 5000),
          withTimeout(getMatch(projectId, sc.n), 5000),
        ]);
        if (cancelled) return;
        setApp((s) => {
          const nextTakes = { ...s.takes };
          const nextMatches = { ...s.matches };
          const nextMatchLoading = { ...s.matchLoading };
          const nextSel = { ...s.selScenes };
          if (t !== null) nextTakes[sc.n] = t;
          if (m !== null) nextMatches[sc.n] = m;
          nextMatchLoading[sc.n] = false;
          nextSel[sc.n] = (nextMatches[sc.n]?.status === "complete");
          // Persist for next reload
          saveCachedMatches(projectId, nextTakes, nextMatches);
          return { ...s, takes: nextTakes, matches: nextMatches, matchLoading: nextMatchLoading, selScenes: nextSel };
        });
      }));
    })();
    return () => { cancelled = true; };
  }, [projectId, navigate]);

  if (screen === "loading" || !app.envelope) {
    return (
      <ScreenShell>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ font: `400 14px ${TOKENS.font}`, color: TOKENS.mutedPlum }}>Opening the studio…</div>
        </div>
      </ScreenShell>
    );
  }

  const env = app.envelope;
  const scenes = env.actor_scenes;
  const currentScene: ActorScene | undefined = scenes.find((s) => s.n === app.currentSceneN);

  const setSceneTake = (sceneN: number, take: SceneTake | null) =>
    setApp((s) => ({ ...s, takes: { ...s.takes, [sceneN]: take } }));
  const setSceneMatch = (sceneN: number, match: MatchStatus) =>
    setApp((s) => ({ ...s, matches: { ...s.matches, [sceneN]: match }, selScenes: { ...s.selScenes, [sceneN]: (match.status === "complete") } }));

  const handlers = {
    enterScene: (n: number) => {
      // Route to the right screen based on this scene's state:
      //   matched  → Matched (review / re-record / run lines from here)
      //   recorded → Match (resume matching)
      //   ready    → Prep (record)
      // Per Ryan iPhone test 2026-06-26: tapping a Matched scene was
      // taking him to Prep/Record which obliterates the existing take.
      const m = app.matches[n];
      const t = app.takes[n];
      const target: Screen = m?.status === "complete" ? "matched"
                            : t ? "match"
                            : "prep";
      setApp((s) => ({ ...s, currentSceneN: n }));
      setScreen(target);
    },
    goBooth: () => setScreen("booth"),
    goRec: () => { setApp((s) => ({ ...s, recElapsedS: 0 })); setScreen("rec"); },
    goSaved: (durMs: number) => { setApp((s) => ({ ...s, savedDurMs: durMs })); setScreen("saved"); },
    goMatch: () => setScreen("match"),
    goMatched: () => setScreen("matched"),
    goReview: () => setScreen("review"),
    goRerecordLine: (lineId: number) => { setApp((s) => ({ ...s, currentLineId: lineId })); setScreen("rerecord-line"); },
    goSelect: () => setScreen("select"),
    goCastLoad: () => setScreen("cast-load"),
    goSetup: () => setScreen("setup"),
    goPlayer: () => setScreen("player"),
    goDone: () => setScreen("done"),
    toggleSel: (n: number) => setApp((s) => ({ ...s, selScenes: { ...s.selScenes, [n]: !s.selScenes[n] } })),
    toggleOthers: () => setApp((s) => ({ ...s, tOthers: !s.tOthers })),
    toggleDesc: () => setApp((s) => ({ ...s, tDesc: !s.tDesc })),
    toggleSasha: () => setApp((s) => ({ ...s, playSasha: !s.playSasha })),
    setPause: (p: "off" | "short" | "long") => setApp((s) => ({ ...s, pauseBefore: p })),
  };

  // ─── SCREEN: Scene Booth ─────────────────────────────────
  if (screen === "booth") {
    return (
      <BoothScreen
        title={env.title ?? "Off Book"} role={role} scenes={scenes}
        takes={app.takes} matches={app.matches} matchLoading={app.matchLoading}
        anyMatched={scenes.some((sc) => app.matches[sc.n]?.status === "complete")}
        onEnter={handlers.enterScene}
        onRunLines={handlers.goSelect}
        onClose={() => navigate("/library")}
      />
    );
  }

  if (!currentScene) return null;
  const currentTake = app.takes[currentScene.n] ?? null;
  const currentMatch = app.matches[currentScene.n] ?? { status: "not_started" as const, progress: 0 };

  // ─── SCREEN: Scene Prep ──────────────────────────────────
  if (screen === "prep") {
    return (
      <PrepScreen
        scene={currentScene} role={role}
        onRecord={handlers.goRec}
        onBack={handlers.goBooth}
      />
    );
  }

  // ─── SCREEN: Recording Active ────────────────────────────
  if (screen === "rec") {
    return (
      <RecScreen
        projectId={projectId!} scene={currentScene} role={role}
        onSaved={(take, durMs) => { setSceneTake(currentScene.n, take); handlers.goSaved(durMs); }}
        onCancel={handlers.goBooth}
      />
    );
  }

  // ─── SCREEN: Scene Saved ─────────────────────────────────
  if (screen === "saved") {
    return (
      <SavedScreen
        scene={currentScene} durMs={app.savedDurMs}
        onContinue={handlers.goMatch}
        onRerecord={handlers.goRec}
      />
    );
  }

  // ─── SCREEN: Matching ────────────────────────────────────
  if (screen === "match") {
    return (
      <MatchScreen
        projectId={projectId!} scene={currentScene}
        match={currentMatch} setMatch={(m) => setSceneMatch(currentScene.n, m)}
        onComplete={handlers.goMatched}
      />
    );
  }

  // ─── SCREEN: Scene Matched ───────────────────────────────
  if (screen === "matched") {
    const nextScene = scenes.find((s) => s.n > currentScene.n);
    const flaggedCount = (currentMatch.manifest ?? []).filter((m) => lineConfidence(m) < FLAG_THRESHOLD).length;
    return (
      <MatchedScreen
        scene={currentScene} role={role}
        canRunLines={scenes.some((sc) => app.matches[sc.n]?.status === "complete")}
        nextScene={nextScene ?? null}
        flaggedCount={flaggedCount}
        onReview={handlers.goReview}
        onRecordNext={() => nextScene ? handlers.enterScene(nextScene.n) : handlers.goBooth()}
        onRunLines={handlers.goSelect}
        onBackToBooth={handlers.goBooth}
      />
    );
  }

  // ─── SCREEN: Review Lines ────────────────────────────────
  if (screen === "review") {
    return (
      <ReviewLinesScreen
        scene={currentScene} match={currentMatch} envelope={env} role={role}
        justClean={app.justCleanedByScene[currentScene.n] ?? new Set<number>()}
        onPickLine={handlers.goRerecordLine}
        onBack={() => setScreen("matched")}
        onDone={handlers.goBooth}
      />
    );
  }

  // ─── SCREEN: Re-record one line ──────────────────────────
  if (screen === "rerecord-line" && app.currentLineId != null) {
    const lineId = app.currentLineId;
    return (
      <RerecordLineScreen
        projectId={projectId!} sceneN={currentScene.n} lineId={lineId}
        envelope={env} role={role}
        onSaved={(updatedManifest) => {
          // Patch the match + mark the line as CLEAN immediately. Backend
          // doesn't re-run ASR after a splice (timestamps shift, but
          // matched/expected stays put), so we'd otherwise still flag the
          // line. Treat the actor's re-record as authoritative.
          const updated: MatchStatus = { ...currentMatch, manifest: updatedManifest };
          setSceneMatch(currentScene.n, updated);
          setApp((s) => {
            const prior = s.justCleanedByScene[currentScene.n] ?? new Set<number>();
            const next = new Set(prior);
            next.add(lineId);
            return { ...s, justCleanedByScene: { ...s.justCleanedByScene, [currentScene.n]: next } };
          });
          setScreen("review");
        }}
        onCancel={() => setScreen("review")}
      />
    );
  }

  // ─── SCREEN: Scene Selection ─────────────────────────────
  if (screen === "select") {
    const matchedScenes = scenes.filter((sc) => app.matches[sc.n]?.status === "complete");
    return (
      <SelectScreen
        scenes={matchedScenes} sel={app.selScenes}
        onToggle={handlers.toggleSel}
        // Per Ryan 2026-06-26: skip the Cast Loading detour by default
        // (Run Lines should just play her back). Cast synthesis is
        // triggered later if she opts into "Read other characters aloud."
        onContinue={handlers.goSetup}
        onBack={handlers.goBooth}
      />
    );
  }

  // ─── SCREEN: Cast Loading (auto-assign + synthesize partner voices) ──
  // Reachable only from Setup when the actor opts into "Read other
  // characters aloud" — no longer in the default Run Lines path.
  if (screen === "cast-load") {
    return (
      <CastLoadScreen
        projectId={projectId!} role={role} envelope={env}
        cast={app.cast} synth={app.castSynth}
        setCast={(c) => setApp((s) => ({ ...s, cast: c }))}
        setSynth={(st) => setApp((s) => ({ ...s, castSynth: st }))}
        onReady={handlers.goPlayer}
        onBack={handlers.goSetup}
      />
    );
  }

  // ─── SCREEN: Playback Setup ──────────────────────────────
  if (screen === "setup") {
    return (
      <SetupScreen
        tOthers={app.tOthers} tDesc={app.tDesc} playSasha={app.playSasha} pauseBefore={app.pauseBefore}
        onToggleOthers={handlers.toggleOthers}
        onToggleDesc={handlers.toggleDesc}
        onToggleSasha={handlers.toggleSasha}
        onSetPause={handlers.setPause}
        // Route through Cast Loading ONLY when partners are turned on.
        // Otherwise go straight to Player (just-play-my-recording fast path).
        onStart={() => app.tOthers ? handlers.goCastLoad() : handlers.goPlayer()}
        onBack={handlers.goSelect}
      />
    );
  }

  // ─── SCREEN: Run Lines Player ────────────────────────────
  if (screen === "player") {
    const scenesToPlay = scenes.filter((sc) => app.selScenes[sc.n] && app.matches[sc.n]?.status === "complete");
    return (
      <PlayerScreen
        scenes={scenesToPlay} takes={app.takes} matches={app.matches}
        cast={app.cast}
        role={role}
        playSasha={app.playSasha}
        tOthers={app.tOthers}
        pauseBefore={app.pauseBefore}
        envelope={env}
        onDone={handlers.goDone}
        onExit={handlers.goBooth}
        title={env.title ?? ""}
      />
    );
  }

  // ─── SCREEN: Run Complete ────────────────────────────────
  if (screen === "done") {
    const sceneCount = Object.values(app.selScenes).filter(Boolean).length;
    return (
      <DoneScreen
        sceneCount={sceneCount}
        playSasha={app.playSasha}
        tDesc={app.tDesc}
        onRunAgain={handlers.goPlayer}
        onChangeScenes={handlers.goSelect}
        onEditSetup={handlers.goSetup}
        onExit={handlers.goBooth}
      />
    );
  }

  return null;
}

// ─────────────────────────────────────────────────────────────
// Screen components
// ─────────────────────────────────────────────────────────────

function BoothScreen({
  title, role, scenes, takes, matches, matchLoading, anyMatched, onEnter, onRunLines, onClose,
}: {
  title: string; role: string; scenes: ActorScene[];
  takes: Record<number, SceneTake | null>;
  matches: Record<number, MatchStatus>;
  matchLoading: Record<number, boolean>;
  anyMatched: boolean;
  onEnter: (n: number) => void;
  onRunLines: () => void;
  onClose: () => void;
}) {
  const totalMin = scenes.reduce((a, s) => a + s.est_minutes, 0);
  const totalLines = scenes.reduce((a, s) => a + s.line_count, 0);

  return (
    <ScreenShell>
      <div style={{ flex: 1, padding: "12px 22px 22px", display: "flex", flexDirection: "column", animation: "scIn .5s ease both", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Label>RECORDING BOOTH</Label>
          <button onClick={onClose} style={{ appearance: "none", border: "none", background: "none", cursor: "pointer", padding: 4, color: TOKENS.mutedPlum, font: `500 13px ${TOKENS.font}` }}>✕</button>
        </div>
        <ScreenTitle style={{ marginTop: 8 }}>{title}</ScreenTitle>
        <div style={{ font: `400 13.5px ${TOKENS.font}`, color: "#3a3543", opacity: 0.78, marginTop: 4 }}>As {role}</div>

        <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          {scenes.map((sc) => {
            const take = takes[sc.n];
            const match = matches[sc.n];
            const loading = matchLoading[sc.n];
            const status = match?.status === "complete" ? "Matched · tap to review"
                         : take ? "Recorded · tap to match"
                         : loading ? "Checking…"
                         : "Ready · tap to record";
            const statusColor = match?.status === "complete" ? TOKENS.success
                              : take ? TOKENS.coral
                              : loading ? TOKENS.mutedPlum
                              : TOKENS.mutedPlum;
            return (
              <button
                key={sc.n}
                onClick={() => onEnter(sc.n)}
                style={{
                  appearance: "none", border: "none", cursor: "pointer", textAlign: "left",
                  padding: 16, borderRadius: 20,
                  background: TOKENS.glass,
                  backdropFilter: "blur(22px) saturate(150%)",
                  WebkitBackdropFilter: "blur(22px) saturate(150%)",
                  borderTop: `1px solid ${TOKENS.glassStrong}`,
                  boxShadow: "0 10px 26px rgba(32,32,42,0.08)",
                  display: "flex", alignItems: "center", gap: 14,
                }}
              >
                <div style={{
                  width: 44, height: 56, borderRadius: 10, background: COVER_GRADIENT,
                  display: "flex", alignItems: "center", justifyContent: "center", flex: "none",
                  color: "#fff", font: `600 14.5px ${TOKENS.font}`, letterSpacing: "0.04em",
                }}>{sc.label.replace("SC ", "")}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ font: `500 15px ${TOKENS.font}`, color: TOKENS.deepInk, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {sc.label}: {sc.name}
                  </div>
                  <div style={{ font: `400 11.5px ${TOKENS.font}`, color: TOKENS.mutedPlum, marginTop: 3 }}>
                    {sc.characters.join(" · ")} · {sc.line_count} lines · ~{sc.est_minutes} min
                  </div>
                  <div style={{ font: `500 11.5px ${TOKENS.font}`, color: statusColor, marginTop: 5 }}>
                    {status}
                  </div>
                </div>
                <div style={{ color: TOKENS.mutedPlum, font: `400 18px ${TOKENS.font}`, flex: "none" }}>›</div>
              </button>
            );
          })}
        </div>

        <GlassCard style={{ marginTop: 18, padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", font: `400 12.5px ${TOKENS.font}`, color: TOKENS.mutedPlum }}>
            <span>{scenes.length} scenes</span><span>~{totalMin} min total</span><span>{totalLines} lines</span>
          </div>
        </GlassCard>
      </div>

      <div style={{ padding: "0 22px 18px" }}>
        {anyMatched ? (
          <PrimaryButton onClick={onRunLines}>Run Lines</PrimaryButton>
        ) : (
          <div style={{ textAlign: "center", font: `400 11.5px ${TOKENS.font}`, color: TOKENS.mutedPlum, padding: 10 }}>
            Record + match at least one scene to start running lines.
          </div>
        )}
      </div>
    </ScreenShell>
  );
}

function PrepScreen({ scene, role, onRecord, onBack }:
  { scene: ActorScene; role: string; onRecord: () => void; onBack: () => void }) {
  return (
    <ScreenShell>
      <div style={{ padding: "12px 22px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button onClick={onBack} style={{ appearance: "none", border: "none", background: "none", cursor: "pointer", color: TOKENS.mutedPlum, font: `500 13px ${TOKENS.font}` }}>‹ Booth</button>
        <Label>{scene.label}</Label>
      </div>

      <div style={{ flex: 1, padding: "22px 22px 18px", display: "flex", flexDirection: "column", animation: "scIn .5s ease both", overflowY: "auto" }}>
        <ScreenTitle>{scene.name}</ScreenTitle>
        <div style={{ font: `400 13.5px ${TOKENS.font}`, color: TOKENS.mutedPlum, marginTop: 5 }}>{scene.slug}</div>

        <GlassCard style={{ marginTop: 22, padding: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Stat label="Est. time" value={`~${scene.est_minutes} min`} />
            <Stat label="Dialogue lines" value={String(scene.line_count)} />
            <Stat label="Your role" value={role} />
            <Stat label="With" value={scene.characters.filter((c) => c !== role).join(", ") || "—"} />
          </div>
        </GlassCard>

        <GlassCard style={{ marginTop: 14, padding: 18 }}>
          <Label>BEFORE YOU BEGIN</Label>
          <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0", display: "flex", flexDirection: "column", gap: 11 }}>
            {[
              "Hold your paper sides — read directly from them.",
              "Read naturally, full performance.",
              "Read action descriptions aloud too.",
              "Take pauses naturally. We're listening.",
              "Tap Stop when you reach the end.",
            ].map((line, i) => (
              <li key={i} style={{ display: "flex", gap: 10, font: `400 14px ${TOKENS.font}`, color: "#3a3543", lineHeight: 1.45 }}>
                <span style={{ flex: "none", width: 18, height: 18, borderRadius: "50%", background: PRIMARY_GRADIENT, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", font: `600 11px ${TOKENS.font}`, marginTop: 1 }}>{i + 1}</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </GlassCard>
      </div>

      <div style={{ padding: "0 22px 18px" }}>
        <PrimaryButton onClick={onRecord}>Record {scene.label}</PrimaryButton>
      </div>
    </ScreenShell>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ font: `600 9.5px ${TOKENS.font}`, letterSpacing: "0.16em", color: TOKENS.mutedPlum, textTransform: "uppercase" }}>{label}</div>
      <div style={{ font: `500 16px ${TOKENS.font}`, color: TOKENS.deepInk, marginTop: 4 }}>{value}</div>
    </div>
  );
}

type RecState = "idle" | "recording" | "paused" | "uploading";
const REDO_FALLBACK_MS = 8000;
const REDO_MIN_MS = 1500;
// VAD thresholds — same primitive as the v0 RecordingRoom. Tuned for
// phone-mic gain at typical conversational distance.
const SILENCE_THRESHOLD = 0.018;
const SILENCE_HOLD_MS = 600;
const VOICE_ARMING_MS = 220;

function RecScreen({ projectId, scene, role, onSaved, onCancel }:
  { projectId: string; scene: ActorScene; role: string; onSaved: (take: SceneTake, durMs: number) => void; onCancel: () => void }) {
  const [state, setState] = useState<RecState>("idle");
  const [audioElapsedMs, setAudioElapsedMs] = useState(0); // audio-time elapsed (pauses excluded)
  const [skipSpans, setSkipSpans] = useState<{ start_ms: number; end_ms: number }[]>([]);
  const [redoToast, setRedoToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const wallStartMsRef = useRef(0);
  const totalPausedMsRef = useRef(0);
  const pauseStartMsRef = useRef(0);

  // VAD plumbing (invisible — only feeds the smart Redo button)
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const vadStateRef = useRef<"idle" | "voice" | "silence">("idle");
  const voiceStartWallRef = useRef<number | null>(null);
  const silenceStartWallRef = useRef<number | null>(null);
  // Audio-time (in ms) where the current line is judged to have started —
  // the last silence→voice transition. Null until we've seen one.
  const currentLineStartMsRef = useRef<number | null>(null);

  const audioElapsedMsNow = (): number => {
    if (!wallStartMsRef.current) return 0;
    const wallNow = performance.now();
    let total = wallNow - wallStartMsRef.current - totalPausedMsRef.current;
    if (pauseStartMsRef.current > 0) total -= (wallNow - pauseStartMsRef.current);
    return Math.max(0, Math.round(total));
  };

  function startVADLoop() {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const buf = new Uint8Array(analyser.fftSize);
    const tick = () => {
      if (!analyserRef.current) return;
      analyser.getByteTimeDomainData(buf);
      let amp = 0;
      const bands = 40;
      const step = Math.floor(buf.length / bands);
      for (let i = 0; i < bands; i++) {
        let mx = 0;
        for (let j = 0; j < step; j++) {
          const v = Math.abs(buf[i * step + j] - 128) / 128;
          if (v > mx) mx = v;
        }
        amp += mx;
      }
      amp /= bands;

      const wallNow = performance.now();
      if (amp > SILENCE_THRESHOLD) {
        // Voice present
        if (voiceStartWallRef.current === null) voiceStartWallRef.current = wallNow;
        // If we were in silence, this is the silence→voice transition.
        // Mark current line start at the audio-time of THIS moment.
        if (vadStateRef.current === "silence") {
          currentLineStartMsRef.current = audioElapsedMsNow();
        }
        vadStateRef.current = "voice";
        silenceStartWallRef.current = null;
      } else {
        // Below threshold
        if (silenceStartWallRef.current === null) silenceStartWallRef.current = wallNow;
        const heldSilence = wallNow - silenceStartWallRef.current;
        const heldVoice = voiceStartWallRef.current !== null ? wallNow - voiceStartWallRef.current : 0;
        if (
          vadStateRef.current === "voice" &&
          voiceStartWallRef.current !== null &&
          heldVoice >= VOICE_ARMING_MS &&
          heldSilence >= SILENCE_HOLD_MS
        ) {
          // voice→silence transition — line ended
          vadStateRef.current = "silence";
          voiceStartWallRef.current = null;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }

  async function start() {
    setError(null);
    setSkipSpans([]);
    chunksRef.current = [];
    totalPausedMsRef.current = 0;
    pauseStartMsRef.current = 0;
    vadStateRef.current = "idle";
    voiceStartWallRef.current = null;
    silenceStartWallRef.current = null;
    currentLineStartMsRef.current = null;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const ctx = new AudioContext();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;

      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        const durationMs = audioElapsedMsNow();
        setState("uploading");
        try {
          const take = await postSceneTake(projectId, scene.n, blob, { durationMs, skipSpans });
          onSaved(take, durationMs);
        } catch (e: any) {
          setError("Upload failed: " + (e?.message ?? "unknown"));
          setState("paused");
        }
      };
      mediaRecorderRef.current = rec;
      wallStartMsRef.current = performance.now();
      rec.start(250);
      startVADLoop();
      setState("recording");
    } catch (e: any) {
      setError(e?.message ?? "couldn't access mic");
    }
  }

  function pauseRec() {
    try { mediaRecorderRef.current?.pause(); } catch { /* no-op */ }
    pauseStartMsRef.current = performance.now();
    setState("paused");
  }

  function resumeRec() {
    if (pauseStartMsRef.current > 0) {
      totalPausedMsRef.current += performance.now() - pauseStartMsRef.current;
      pauseStartMsRef.current = 0;
    }
    try { mediaRecorderRef.current?.resume(); } catch { /* no-op */ }
    setState("recording");
  }

  function redoLastBit() {
    const end = audioElapsedMsNow();
    // Prefer the smart anchor: most recent silence→voice transition. If
    // there isn't one yet (no detected pauses), fall back to the fixed
    // window. Either way, enforce a sensible minimum so the button does
    // something useful even in fast-talking edge cases.
    const lineStart = currentLineStartMsRef.current;
    let start: number;
    let mode: "line" | "fallback";
    if (lineStart !== null && end - lineStart >= REDO_MIN_MS) {
      start = lineStart;
      mode = "line";
    } else {
      start = Math.max(0, end - REDO_FALLBACK_MS);
      mode = "fallback";
    }
    if (end <= start) return;
    const cutSec = Math.round((end - start) / 1000);
    setSkipSpans((prev) => [...prev, { start_ms: start, end_ms: end }]);
    // Reset the line-start anchor so the next "redo" picks the next line
    // boundary (otherwise stacking redos would all snap to the same start).
    currentLineStartMsRef.current = null;
    setRedoToast(mode === "line"
      ? `Cutting last ${cutSec}s (your current line).`
      : `Cutting last ${cutSec}s.`);
    window.setTimeout(() => setRedoToast(null), 2000);
  }

  function tearDownAudio() {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    try { audioCtxRef.current?.close(); } catch { /* no-op */ }
    audioCtxRef.current = null;
    analyserRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }

  function stopRec() {
    if (pauseStartMsRef.current > 0) {
      totalPausedMsRef.current += performance.now() - pauseStartMsRef.current;
      pauseStartMsRef.current = 0;
    }
    try { mediaRecorderRef.current?.stop(); } catch { /* no-op */ }
    tearDownAudio();
    // onstop handler picks up from here (sets state to "uploading")
  }

  function discard() {
    try { mediaRecorderRef.current?.stop(); } catch { /* no-op */ }
    tearDownAudio();
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    wallStartMsRef.current = 0;
    totalPausedMsRef.current = 0;
    pauseStartMsRef.current = 0;
    currentLineStartMsRef.current = null;
    setSkipSpans([]);
    setAudioElapsedMs(0);
    setState("idle");
  }

  // Tick the displayed elapsed time
  useEffect(() => {
    if (state !== "recording" && state !== "paused") return;
    const id = window.setInterval(() => setAudioElapsedMs(audioElapsedMsNow()), 250);
    return () => window.clearInterval(id);
  }, [state]);

  // Mic cleanup if unmounted mid-record
  useEffect(() => {
    return () => {
      try { mediaRecorderRef.current?.stop(); } catch { /* no-op */ }
      tearDownAudio();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const elapsedDisplay = fmtTime(Math.floor(audioElapsedMs / 1000));
  const live = state === "recording";
  const paused = state === "paused";
  const idle = state === "idle";
  const uploading = state === "uploading";

  return (
    <ScreenShell>
      <div style={{ padding: "12px 22px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Label>RECORDING · {scene.label}</Label>
        {(idle) && (
          <button onClick={onCancel} style={{ appearance: "none", border: "none", background: "none", cursor: "pointer", color: TOKENS.mutedPlum, font: `500 13px ${TOKENS.font}` }}>✕</button>
        )}
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 24px", gap: 22, textAlign: "center", position: "relative" }}>
        {live || paused ? (
          <>
            <div style={{ position: "relative", width: 160, height: 160, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: PRIMARY_GRADIENT, opacity: live ? 0.18 : 0.08, animation: live ? "pulse 2.4s ease-in-out infinite" : "none", transition: "opacity .4s" }} />
              <div style={{ width: 14, height: 14, borderRadius: "50%", background: live ? TOKENS.coral : TOKENS.mutedPlum, animation: live ? "blink 1.4s ease-in-out infinite" : "none" }} />
            </div>
            <div style={{ font: `300 56px ${TOKENS.font}`, letterSpacing: "-0.02em", color: TOKENS.deepInk, fontVariantNumeric: "tabular-nums" }}>{elapsedDisplay}</div>
            <div style={{ font: `400 13px ${TOKENS.font}`, color: TOKENS.mutedPlum, maxWidth: 280, lineHeight: 1.5 }}>
              {paused
                ? `Paused. Tap Resume to keep reading.`
                : `Reading ${scene.label} — ${scene.name}.`}
              {!paused && <><br />No scrolling · No line tracking · No transcription.</>}
            </div>
            {skipSpans.length > 0 && (
              <div style={{ font: `400 11px ${TOKENS.font}`, color: TOKENS.mutedPlum, opacity: 0.7 }}>
                {skipSpans.length} redo cut{skipSpans.length === 1 ? "" : "s"} pending — we'll trim them on save.
              </div>
            )}
          </>
        ) : uploading ? (
          <>
            <div style={{ width: 60, height: 60, borderRadius: "50%", border: `3px solid ${TOKENS.coral}`, borderTopColor: "transparent", animation: "spin 1.25s linear infinite" }} />
            <div style={{ font: `400 14px ${TOKENS.font}`, color: TOKENS.mutedPlum }}>Saving your take…</div>
          </>
        ) : (
          <>
            <div style={{ font: `300 24px ${TOKENS.font}`, color: TOKENS.deepInk }}>Ready when you are.</div>
            <div style={{ font: `400 13px ${TOKENS.font}`, color: TOKENS.mutedPlum, maxWidth: 260, lineHeight: 1.5 }}>
              Hold your sides. Read all dialogue + descriptions naturally.<br />
              You can pause, or tap Redo if you flub a line.
            </div>
          </>
        )}
        {error && <div style={{ font: `400 12px ${TOKENS.font}`, color: TOKENS.coral, maxWidth: 280 }}>{error}</div>}

        {/* Redo confirmation toast */}
        {redoToast && (
          <div style={{ position: "absolute", bottom: 10, padding: "9px 16px", borderRadius: 14, background: "rgba(32,32,42,0.85)", color: "#fff", font: `500 12.5px ${TOKENS.font}`, animation: "scIn .25s ease both", maxWidth: 320, textAlign: "center" }}>
            {redoToast}
          </div>
        )}
      </div>

      <div style={{ padding: "0 22px 22px", display: "flex", flexDirection: "column", gap: 10 }}>
        {idle && <PrimaryButton onClick={start}>Start Recording</PrimaryButton>}
        {uploading && <PrimaryButton disabled>Saving…</PrimaryButton>}
        {live && (
          <>
            <div style={{ display: "flex", gap: 10 }}>
              <SecondaryButton onClick={redoLastBit} style={{ width: "auto", flex: 1 }}>↺ Redo last bit</SecondaryButton>
              <SecondaryButton onClick={pauseRec} style={{ width: "auto", flex: 1 }}>❚❚ Pause</SecondaryButton>
            </div>
            <PrimaryButton onClick={stopRec}>Stop & Save</PrimaryButton>
          </>
        )}
        {paused && (
          <>
            <div style={{ display: "flex", gap: 10 }}>
              <SecondaryButton onClick={redoLastBit} style={{ width: "auto", flex: 1 }}>↺ Redo last bit</SecondaryButton>
              <SecondaryButton onClick={discard} style={{ width: "auto", flex: 1 }}>Discard</SecondaryButton>
            </div>
            <PrimaryButton onClick={resumeRec}>▶ Resume</PrimaryButton>
            <SecondaryButton onClick={stopRec}>Stop & Save</SecondaryButton>
          </>
        )}
      </div>
    </ScreenShell>
  );
}

function SavedScreen({ scene, durMs, onContinue, onRerecord }:
  { scene: ActorScene; durMs: number; onContinue: () => void; onRerecord: () => void }) {
  return (
    <ScreenShell>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 32px", textAlign: "center", gap: 22, animation: "scIn .5s ease both" }}>
        <div style={{ width: 96, height: 96, borderRadius: "50%", background: PRIMARY_GRADIENT, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 14px 32px rgba(242,95,87,.4)" }}>
          <CheckIcon size={44} />
        </div>
        <ScreenTitle>Take saved</ScreenTitle>
        <div style={{ font: `400 14px ${TOKENS.font}`, color: "#3a3543", opacity: 0.8, lineHeight: 1.5, maxWidth: 280 }}>
          {scene.label} · {scene.name}<br />
          {fmtTime(Math.round(durMs / 1000))} recorded
        </div>
      </div>
      <div style={{ padding: "0 22px 22px", display: "flex", flexDirection: "column", gap: 10 }}>
        <PrimaryButton onClick={onContinue}>Continue</PrimaryButton>
        <SecondaryButton onClick={onRerecord}>Re-record</SecondaryButton>
      </div>
    </ScreenShell>
  );
}

function MatchScreen({ projectId, scene, match, setMatch, onComplete }:
  { projectId: string; scene: ActorScene; match: MatchStatus; setMatch: (m: MatchStatus) => void; onComplete: () => void }) {

  // Kick off the match if not already running
  useEffect(() => {
    if (match.status === "complete") return;
    if (match.status === "starting" || match.status === "asr_done" || match.status === "aligning") return;
    let cancelled = false;
    (async () => {
      try {
        await startMatch(projectId, scene.n);
      } catch (e: any) {
        if (!cancelled) setMatch({ status: "error", progress: 0, error: e?.message ?? "match failed" });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll
  useEffect(() => {
    if (match.status === "complete" || match.status === "error") return;
    let cancelled = false;
    const tick = async () => {
      try {
        const m = await getMatch(projectId, scene.n);
        if (cancelled) return;
        setMatch(m);
        if (m.status === "complete") {
          window.setTimeout(() => { if (!cancelled) onComplete(); }, 800);
        }
      } catch { /* swallow, retry */ }
    };
    const id = window.setInterval(tick, 800);
    tick();
    return () => { cancelled = true; window.clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.status]);

  const items = [
    "Reading scene description",
    "Aligning your dialogue",
    "Matching " + (scene.characters.find((c) => c !== "SASHA") || "other characters"),
    "Pinning timing markers",
    "Finalizing the alignment",
  ];
  const progress = match.progress ?? 0;

  return (
    <ScreenShell>
      <div style={{ padding: "12px 22px 0" }}>
        <Label>MATCHING · {scene.label}</Label>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 24px", gap: 24, textAlign: "center" }}>
        {match.status === "complete" ? (
          <div style={{ width: 92, height: 92, borderRadius: "50%", background: PRIMARY_GRADIENT, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 14px 32px rgba(242,95,87,.4)", animation: "scIn .5s ease both" }}>
            <CheckIcon size={44} />
          </div>
        ) : (
          <div style={{ position: "relative", width: 96, height: 96, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{
              position: "absolute", inset: 0, borderRadius: "50%",
              background: `conic-gradient(from 0deg, transparent, ${TOKENS.coral})`,
              animation: "spin 1.25s linear infinite",
              WebkitMask: "radial-gradient(farthest-side,transparent 62%,#000 64%)",
              mask: "radial-gradient(farthest-side,transparent 62%,#000 64%)",
            }} />
          </div>
        )}
        <ScreenTitle>{match.status === "complete" ? "Scene matched" : "Listening to your take"}</ScreenTitle>
        <div style={{ font: `400 13.5px ${TOKENS.font}`, color: TOKENS.mutedPlum, maxWidth: 280, lineHeight: 1.5 }}>
          {match.status === "complete" ? "Everything aligned cleanly." : "We're matching your audio to the script."}
        </div>

        <GlassCard style={{ width: "100%", maxWidth: 320, padding: 16, marginTop: 6 }}>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 9 }}>
            {items.map((label, i) => {
              const done = i < progress;
              return (
                <li key={i} style={{ display: "flex", gap: 10, alignItems: "center", font: `400 13.5px ${TOKENS.font}`, color: done ? TOKENS.deepInk : TOKENS.mutedPlum }}>
                  {done ? <CircleIcon size={16} /> : <div style={{ width: 16, height: 16, borderRadius: "50%", border: `1.5px solid ${TOKENS.mutedPlum}`, opacity: 0.5 }} />}
                  <span>{label}</span>
                </li>
              );
            })}
          </ul>
        </GlassCard>

        {match.status === "error" && (
          <div style={{ font: `400 13px ${TOKENS.font}`, color: TOKENS.coral, maxWidth: 280 }}>{match.error || "Matching failed."}</div>
        )}
      </div>
      <div style={{ padding: "0 22px 22px" }}>
        <PrimaryButton onClick={onComplete} disabled={match.status !== "complete"}>
          Continue
        </PrimaryButton>
      </div>
    </ScreenShell>
  );
}

function MatchedScreen({ scene, role, canRunLines, nextScene, flaggedCount, onReview, onRecordNext, onRunLines, onBackToBooth }: {
  scene: ActorScene; role: string; canRunLines: boolean; nextScene: ActorScene | null;
  flaggedCount: number; onReview: () => void;
  onRecordNext: () => void; onRunLines: () => void; onBackToBooth: () => void;
}) {
  return (
    <ScreenShell>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 28px", textAlign: "center", gap: 18 }}>
        <div style={{ width: 96, height: 96, borderRadius: "50%", background: PRIMARY_GRADIENT, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 14px 32px rgba(242,95,87,.4)" }}>
          <CheckIcon size={44} />
        </div>
        <ScreenTitle>{scene.label} is ready</ScreenTitle>
        <div style={{ font: `400 14px ${TOKENS.font}`, color: "#3a3543", opacity: 0.85, lineHeight: 1.5, maxWidth: 280 }}>
          Your rehearsal partner is now ready for {scene.name}.
        </div>
        {flaggedCount > 0 && (
          <button
            onClick={onReview}
            style={{
              appearance: "none", cursor: "pointer", marginTop: 4,
              padding: "12px 18px", borderRadius: 16,
              background: "rgba(255,255,255,0.32)", border: `1px solid ${TOKENS.glassStrong}`,
              color: TOKENS.deepInk, font: `500 13px ${TOKENS.font}`,
              backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
              display: "flex", alignItems: "center", gap: 8,
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: TOKENS.coral }} />
            {flaggedCount} line{flaggedCount === 1 ? "" : "s"} need a second look →
          </button>
        )}
      </div>

      <div style={{ padding: "0 22px 22px", display: "flex", flexDirection: "column", gap: 10 }}>
        {nextScene ? (
          <PrimaryButton onClick={onRecordNext}>Record {nextScene.label}</PrimaryButton>
        ) : canRunLines ? (
          <PrimaryButton onClick={onRunLines}>Run Lines</PrimaryButton>
        ) : null}
        {canRunLines && nextScene && (
          <SecondaryButton onClick={onRunLines}>Run Lines</SecondaryButton>
        )}
        <SecondaryButton onClick={onReview}>Review {scene.label}</SecondaryButton>
        <SecondaryButton onClick={onBackToBooth}>Back to Booth</SecondaryButton>
      </div>
    </ScreenShell>
  );
}

// ─────────────────────────────────────────────────────────────
// Review Lines — list each matched line with confidence chip
// ─────────────────────────────────────────────────────────────

function ReviewLinesScreen({ scene, match, envelope, role, justClean, onPickLine, onBack, onDone }: {
  scene: ActorScene; match: MatchStatus; envelope: ActorScenesEnvelope; role: string;
  justClean: Set<number>;
  onPickLine: (lineId: number) => void; onBack: () => void; onDone: () => void;
}) {
  const manifest = (match.manifest ?? []).slice().sort((a, b) => a.start_ms - b.start_ms);
  const aligned = new Set(manifest.map((m) => m.line_id));
  // Compose the row data, preserving SCENE line order from the actor_scene
  const rows = scene.line_ids.map((lid) => {
    const m = manifest.find((mm) => mm.line_id === lid);
    const ch = envelope.line_chars[lid] ?? null;
    const text = envelope.line_texts[lid] ?? "";
    const recentlyCleaned = justClean.has(lid);
    const confidence = recentlyCleaned ? 1 : (m ? lineConfidence(m) : 0);
    const flagged = !!m && !recentlyCleaned && confidence < FLAG_THRESHOLD;
    return { lineId: lid, character: ch, text, manifest: m, confidence, flagged, missing: !aligned.has(lid) && !recentlyCleaned };
  });
  const flaggedCount = rows.filter((r) => r.flagged).length;
  const missedCount = rows.filter((r) => r.missing).length;
  const spokenRows = rows.filter((r) => !r.missing);
  const [showMissed, setShowMissed] = useState(false);
  const displayRows = showMissed ? rows : spokenRows;

  return (
    <ScreenShell>
      <div style={{ padding: "12px 22px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button onClick={onBack} style={{ appearance: "none", border: "none", background: "none", cursor: "pointer", color: TOKENS.mutedPlum, font: `500 13px ${TOKENS.font}` }}>‹ Back</button>
        <Label>REVIEW · {scene.label}</Label>
      </div>

      <div style={{ flex: 1, padding: "20px 22px 0", overflowY: "auto" }}>
        <ScreenTitle>{scene.name}</ScreenTitle>
        <div style={{ font: `400 13.5px ${TOKENS.font}`, color: TOKENS.mutedPlum, marginTop: 5 }}>
          {flaggedCount > 0 ? `${flaggedCount} line${flaggedCount === 1 ? "" : "s"} didn't match the script cleanly.` : "Every line you read matched cleanly. Tap any line to re-record."}
        </div>

        {missedCount > 0 && (
          <button
            onClick={() => setShowMissed((v) => !v)}
            style={{
              appearance: "none", cursor: "pointer", marginTop: 12,
              padding: "8px 12px", borderRadius: 12,
              background: "rgba(255,255,255,0.32)", border: `1px solid ${TOKENS.glassStrong}`,
              color: TOKENS.mutedPlum, font: `500 11px ${TOKENS.font}`,
              backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            {showMissed
              ? `Hide ${missedCount} line${missedCount === 1 ? "" : "s"} I didn't read`
              : `Show ${missedCount} line${missedCount === 1 ? "" : "s"} I didn't read`}
          </button>
        )}

        <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 8 }}>
          {displayRows.map((r) => {
            const isYou = (r.character || "").toUpperCase() === role.toUpperCase();
            const chipBg = r.missing ? "rgba(242,95,87,.16)"
                        : r.flagged ? "rgba(242,95,87,.12)"
                        : "rgba(31,138,91,.12)";
            const chipFg = r.missing || r.flagged ? TOKENS.coral : TOKENS.success;
            const chipText = r.missing ? "MISSED" : r.flagged ? `${Math.round(r.confidence * 100)}%` : "CLEAN";
            return (
              <button
                key={r.lineId}
                onClick={() => onPickLine(r.lineId)}
                disabled={r.missing}
                style={{
                  appearance: "none", cursor: r.missing ? "not-allowed" : "pointer", textAlign: "left",
                  padding: 14, borderRadius: 16,
                  background: TOKENS.glass,
                  backdropFilter: "blur(22px) saturate(150%)",
                  WebkitBackdropFilter: "blur(22px) saturate(150%)",
                  border: r.flagged ? `1.5px solid rgba(242,95,87,.5)` : `1px solid ${TOKENS.glassStrong}`,
                  display: "flex", gap: 10, opacity: r.missing ? 0.55 : 1,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ font: `600 10.5px ${TOKENS.font}`, letterSpacing: "0.15em", color: isYou ? TOKENS.coral : TOKENS.mutedPlum, textTransform: "uppercase" }}>
                    {r.character || "Description"}{isYou ? " · you" : ""}
                  </div>
                  <div style={{ font: `400 13.5px ${TOKENS.font}`, color: TOKENS.deepInk, marginTop: 3, lineHeight: 1.4 }}>
                    {r.text}
                  </div>
                </div>
                <div style={{ padding: "5px 9px", borderRadius: 10, background: chipBg, color: chipFg, font: `700 9.5px ${TOKENS.font}`, letterSpacing: "0.1em", alignSelf: "flex-start", flex: "none" }}>
                  {chipText}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ padding: "16px 22px 22px" }}>
        <PrimaryButton onClick={onDone}>Done</PrimaryButton>
      </div>
    </ScreenShell>
  );
}

// ─────────────────────────────────────────────────────────────
// Re-record a single line
// ─────────────────────────────────────────────────────────────

function RerecordLineScreen({ projectId, sceneN, lineId, envelope, role, onSaved, onCancel }: {
  projectId: string; sceneN: number; lineId: number;
  envelope: ActorScenesEnvelope; role: string;
  onSaved: (manifest: SceneManifestEntry[]) => void;
  onCancel: () => void;
}) {
  const text = envelope.line_texts[lineId] ?? "";
  const ch = envelope.line_chars[lineId] ?? null;
  const isYou = (ch || "").toUpperCase() === role.toUpperCase();

  const [state, setState] = useState<"idle" | "recording" | "uploading">("idle");
  const [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const startMsRef = useRef(0);

  async function start() {
    setError(null);
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setState("uploading");
        try {
          const r = await postLineRetake(projectId, sceneN, lineId, blob);
          onSaved(r.manifest);
        } catch (e: any) {
          setError("Upload failed: " + (e?.message ?? "unknown"));
          setState("idle");
        }
      };
      mediaRecorderRef.current = rec;
      startMsRef.current = performance.now();
      rec.start(250);
      setState("recording");
    } catch (e: any) {
      setError(e?.message ?? "couldn't access mic");
    }
  }
  function stop() {
    try { mediaRecorderRef.current?.stop(); } catch { /* no-op */ }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }
  useEffect(() => {
    if (state !== "recording") return;
    const id = window.setInterval(() => setElapsedMs(performance.now() - startMsRef.current), 200);
    return () => window.clearInterval(id);
  }, [state]);
  useEffect(() => {
    return () => {
      try { mediaRecorderRef.current?.stop(); } catch { /* no-op */ }
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const elapsedSec = Math.floor(elapsedMs / 1000);
  const elapsedDisplay = `${elapsedSec}s`;

  return (
    <ScreenShell>
      <div style={{ padding: "12px 22px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {state === "idle" && <button onClick={onCancel} style={{ appearance: "none", border: "none", background: "none", cursor: "pointer", color: TOKENS.mutedPlum, font: `500 13px ${TOKENS.font}` }}>‹ Cancel</button>}
        {state !== "idle" && <span />}
        <Label>RE-RECORD LINE</Label>
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 28px", gap: 22, textAlign: "center" }}>
        <Label>{ch || "Description"}{isYou ? " · You" : ""}</Label>
        <div style={{ font: `300 22px ${TOKENS.font}`, color: TOKENS.deepInk, lineHeight: 1.4, maxWidth: 320, fontStyle: "italic" }}>
          “{text}”
        </div>

        {state === "recording" && (
          <>
            <div style={{ position: "relative", width: 120, height: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: PRIMARY_GRADIENT, opacity: 0.18, animation: "pulse 2.4s ease-in-out infinite" }} />
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: TOKENS.coral, animation: "blink 1.4s ease-in-out infinite" }} />
            </div>
            <div style={{ font: `400 13px ${TOKENS.font}`, color: TOKENS.mutedPlum }}>
              Recording {elapsedDisplay} · tap Stop when done.
            </div>
          </>
        )}
        {state === "uploading" && (
          <>
            <div style={{ width: 50, height: 50, borderRadius: "50%", border: `3px solid ${TOKENS.coral}`, borderTopColor: "transparent", animation: "spin 1.25s linear infinite" }} />
            <div style={{ font: `400 13px ${TOKENS.font}`, color: TOKENS.mutedPlum }}>Stitching your new take in…</div>
          </>
        )}
        {state === "idle" && (
          <div style={{ font: `400 13px ${TOKENS.font}`, color: TOKENS.mutedPlum, maxWidth: 280, lineHeight: 1.5 }}>
            Speak just this one line. We'll splice it back into your scene at the same spot.
          </div>
        )}
        {error && <div style={{ font: `400 12px ${TOKENS.font}`, color: TOKENS.coral, maxWidth: 280 }}>{error}</div>}
      </div>

      <div style={{ padding: "0 22px 22px" }}>
        {state === "idle" && <PrimaryButton onClick={start}>Record this line</PrimaryButton>}
        {state === "recording" && <PrimaryButton onClick={stop}>Stop & Save</PrimaryButton>}
        {state === "uploading" && <PrimaryButton disabled>Saving…</PrimaryButton>}
      </div>
    </ScreenShell>
  );
}

function SelectScreen({ scenes, sel, onToggle, onContinue, onBack }: {
  scenes: ActorScene[]; sel: Record<number, boolean>;
  onToggle: (n: number) => void; onContinue: () => void; onBack: () => void;
}) {
  const selectedCount = scenes.filter((sc) => sel[sc.n]).length;
  const selectedMin = scenes.reduce((a, sc) => sel[sc.n] ? a + sc.est_minutes : a, 0);

  return (
    <ScreenShell>
      <div style={{ padding: "12px 22px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button onClick={onBack} style={{ appearance: "none", border: "none", background: "none", cursor: "pointer", color: TOKENS.mutedPlum, font: `500 13px ${TOKENS.font}` }}>‹ Booth</button>
        <Label>RUN LINES</Label>
      </div>

      <div style={{ flex: 1, padding: "20px 22px 0", overflowY: "auto" }}>
        <ScreenTitle>Pick your scenes</ScreenTitle>
        <div style={{ font: `400 13.5px ${TOKENS.font}`, color: TOKENS.mutedPlum, marginTop: 5 }}>Choose what to rehearse in this run.</div>

        <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          {scenes.map((sc) => {
            const selected = !!sel[sc.n];
            return (
              <button
                key={sc.n}
                onClick={() => onToggle(sc.n)}
                style={{
                  appearance: "none", cursor: "pointer", textAlign: "left",
                  padding: 16, borderRadius: 20,
                  background: selected ? "rgba(255,255,255,0.32)" : TOKENS.glass,
                  border: selected ? `1.5px solid ${TOKENS.coral}` : `1px solid ${TOKENS.glassStrong}`,
                  backdropFilter: "blur(22px) saturate(150%)",
                  WebkitBackdropFilter: "blur(22px) saturate(150%)",
                  display: "flex", alignItems: "center", gap: 12,
                  boxShadow: "0 10px 26px rgba(32,32,42,0.08)",
                }}
              >
                <div style={{ width: 22, height: 22, borderRadius: 7, border: `1.5px solid ${selected ? TOKENS.coral : TOKENS.mutedPlum}`, background: selected ? TOKENS.coral : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
                  {selected && <CheckIcon size={14} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ font: `500 15px ${TOKENS.font}`, color: TOKENS.deepInk }}>{sc.label} · {sc.name}</div>
                  <div style={{ font: `400 11.5px ${TOKENS.font}`, color: TOKENS.mutedPlum, marginTop: 3 }}>
                    {sc.line_count} lines · ~{sc.est_minutes} min
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ padding: "16px 22px 22px" }}>
        <div style={{ textAlign: "center", font: `400 12.5px ${TOKENS.font}`, color: TOKENS.mutedPlum, marginBottom: 10 }}>
          {selectedCount} scene{selectedCount === 1 ? "" : "s"} · ~{selectedMin} min total
        </div>
        <PrimaryButton onClick={onContinue} disabled={selectedCount === 0}>Continue</PrimaryButton>
      </div>
    </ScreenShell>
  );
}

// ─────────────────────────────────────────────────────────────
// Cast Load — auto-assign voices + pre-synthesize partner lines
// ─────────────────────────────────────────────────────────────

function CastLoadScreen({
  projectId, role, envelope, cast, synth, setCast, setSynth, onReady, onBack,
}: {
  projectId: string; role: string; envelope: ActorScenesEnvelope;
  cast: CastEnvelope | null; synth: CastSynthState | null;
  setCast: (c: CastEnvelope) => void;
  setSynth: (st: CastSynthState) => void;
  onReady: () => void; onBack: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    let cancelled = false;
    (async () => {
      try {
        // Fetch existing cast state
        const existing = await getCast(projectId);
        if (cancelled) return;
        setCast(existing);
        // Kick off synthesis (auto-assigns if needed). It's a no-op for
        // already-cached lines.
        await startSynthesize(projectId, role);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? "couldn't start casting");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Poll synthesis status
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const st = await getSynthStatus(projectId);
        if (cancelled) return;
        setSynth(st);
        if (st.status === "complete") {
          // Refresh cast envelope so the manifest reflects all ready lines
          try {
            const fresh = await getCast(projectId);
            if (!cancelled) setCast(fresh);
          } catch { /* no-op */ }
          window.setTimeout(() => { if (!cancelled) onReady(); }, 800);
        }
        if (st.status === "error") {
          setError(st.error || "synthesis failed");
        }
      } catch { /* swallow, retry */ }
    };
    const id = window.setInterval(tick, 900);
    tick();
    return () => { cancelled = true; window.clearInterval(id); };
  }, [projectId]);

  // Pull the actor's partner characters out for display
  const partners = useMemo(() => {
    if (!cast) return [] as Array<{ character: string; voiceName: string }>;
    return Object.entries(cast.assignments)
      .filter(([ch]) => ch.toUpperCase() !== role.toUpperCase())
      .map(([ch, vid]) => {
        const v = cast.voices_library.find((vv) => vv.voice_id === vid);
        return { character: ch, voiceName: v?.name ?? "Voice" };
      });
  }, [cast, role]);

  const done = synth?.done ?? 0;
  const total = synth?.total ?? 0;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const isComplete = synth?.status === "complete";

  return (
    <ScreenShell>
      <div style={{ padding: "12px 22px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button onClick={onBack} style={{ appearance: "none", border: "none", background: "none", cursor: "pointer", color: TOKENS.mutedPlum, font: `500 13px ${TOKENS.font}` }}>‹ Back</button>
        <Label>CASTING</Label>
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 28px", textAlign: "center", gap: 22 }}>
        {isComplete ? (
          <div style={{ width: 92, height: 92, borderRadius: "50%", background: PRIMARY_GRADIENT, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 14px 32px rgba(242,95,87,.4)", animation: "scIn .5s ease both" }}>
            <CheckIcon size={44} />
          </div>
        ) : (
          <div style={{ position: "relative", width: 96, height: 96, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{
              position: "absolute", inset: 0, borderRadius: "50%",
              background: `conic-gradient(from 0deg, transparent, ${TOKENS.coral})`,
              animation: "spin 1.25s linear infinite",
              WebkitMask: "radial-gradient(farthest-side,transparent 62%,#000 64%)",
              mask: "radial-gradient(farthest-side,transparent 62%,#000 64%)",
            }} />
          </div>
        )}
        <ScreenTitle>{isComplete ? "Cast is ready" : "Casting your scene partners"}</ScreenTitle>
        <div style={{ font: `400 13.5px ${TOKENS.font}`, color: TOKENS.mutedPlum, maxWidth: 280, lineHeight: 1.5 }}>
          {isComplete ? "Tap continue to set up playback." : `Synthesizing partner voices · ${pct}% (${done} of ${total})`}
        </div>

        {partners.length > 0 && (
          <GlassCard style={{ width: "100%", maxWidth: 320, padding: 16, marginTop: 4 }}>
            <Label>VOICE CAST</Label>
            <ul style={{ listStyle: "none", margin: "10px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
              {partners.map((p) => (
                <li key={p.character} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", font: `400 13.5px ${TOKENS.font}`, color: TOKENS.deepInk }}>
                  <span>{p.character}</span>
                  <span style={{ color: TOKENS.mutedPlum }}>{p.voiceName}</span>
                </li>
              ))}
            </ul>
          </GlassCard>
        )}

        {error && <div style={{ font: `400 12.5px ${TOKENS.font}`, color: TOKENS.coral, maxWidth: 280 }}>{error}</div>}
      </div>

      <div style={{ padding: "0 22px 22px" }}>
        <PrimaryButton onClick={onReady} disabled={!isComplete}>Continue</PrimaryButton>
      </div>
    </ScreenShell>
  );
}

function SetupScreen({
  tOthers, tDesc, playSasha, pauseBefore,
  onToggleOthers, onToggleDesc, onToggleSasha, onSetPause, onStart, onBack,
}: {
  tOthers: boolean; tDesc: boolean; playSasha: boolean; pauseBefore: "off" | "short" | "long";
  onToggleOthers: () => void; onToggleDesc: () => void; onToggleSasha: () => void;
  onSetPause: (p: "off" | "short" | "long") => void;
  onStart: () => void; onBack: () => void;
}) {
  return (
    <ScreenShell>
      <div style={{ padding: "12px 22px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button onClick={onBack} style={{ appearance: "none", border: "none", background: "none", cursor: "pointer", color: TOKENS.mutedPlum, font: `500 13px ${TOKENS.font}` }}>‹ Scenes</button>
        <Label>PLAYBACK</Label>
      </div>

      <div style={{ flex: 1, padding: "20px 22px 0", overflowY: "auto" }}>
        <ScreenTitle>How should it run?</ScreenTitle>

        <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 10 }}>
          <ToggleRow title="Read other characters aloud"
            sub="Plays their lines from your take."
            on={tOthers} onToggle={onToggleOthers} />
          <ToggleRow title="Read scene descriptions"
            sub="Action beats between dialogue."
            on={tDesc} onToggle={onToggleDesc} />
          <ToggleRow title="Play your lines"
            sub={playSasha ? "Plays your own delivery." : "Mutes your lines so you speak them live."}
            on={playSasha} onToggle={onToggleSasha} />
        </div>

        <div style={{ marginTop: 22 }}>
          <Label>PAUSE BEFORE YOUR LINES</Label>
          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
            {(["off", "short", "long"] as const).map((opt) => {
              const selected = pauseBefore === opt;
              return (
                <button
                  key={opt}
                  onClick={() => onSetPause(opt)}
                  style={{
                    appearance: "none", cursor: "pointer", flex: 1, padding: "12px 0",
                    background: selected ? PRIMARY_GRADIENT : "rgba(255,255,255,0.32)",
                    border: selected ? "none" : `1px solid ${TOKENS.glassStrong}`,
                    borderRadius: 14,
                    color: selected ? "#fff" : TOKENS.deepInk,
                    font: `600 13px ${TOKENS.font}`, textTransform: "capitalize",
                    backdropFilter: "blur(20px)",
                    WebkitBackdropFilter: "blur(20px)",
                  }}
                >{opt}</button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ padding: "16px 22px 22px" }}>
        <PrimaryButton onClick={onStart}>Start Run</PrimaryButton>
      </div>
    </ScreenShell>
  );
}

function ToggleRow({ title, sub, on, onToggle }: { title: string; sub: string; on: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      style={{
        appearance: "none", cursor: "pointer", textAlign: "left",
        padding: 16, borderRadius: 20,
        background: TOKENS.glass,
        backdropFilter: "blur(22px) saturate(150%)",
        WebkitBackdropFilter: "blur(22px) saturate(150%)",
        border: `1px solid ${TOKENS.glassStrong}`,
        display: "flex", alignItems: "center", gap: 12,
        boxShadow: "0 10px 26px rgba(32,32,42,0.08)",
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ font: `500 14.5px ${TOKENS.font}`, color: TOKENS.deepInk }}>{title}</div>
        <div style={{ font: `400 12px ${TOKENS.font}`, color: TOKENS.mutedPlum, marginTop: 3 }}>{sub}</div>
      </div>
      <div style={{
        width: 42, height: 24, borderRadius: 999,
        background: on ? PRIMARY_GRADIENT : "rgba(32,32,42,.15)",
        position: "relative", transition: "background .2s", flex: "none",
      }}>
        <div style={{
          position: "absolute", top: 2, left: on ? 20 : 2,
          width: 20, height: 20, borderRadius: "50%", background: "#fff",
          transition: "left .2s",
          boxShadow: "0 2px 6px rgba(32,32,42,.2)",
        }} />
      </div>
    </button>
  );
}

// One cue in the playback queue. Sequential — each cue plays then the
// next one starts. Cues come in three flavors per the God Reference §10:
//   self    — actor's recorded segment (her recording, sliced by manifest)
//   cast    — ElevenLabs partner voice mp3
//   silence — quiet gap (for pause-before-my-lines, or muted lines)
type Cue =
  | { type: "self";  lineId: number; sceneIdx: number; audioUrl: string; startMs: number; endMs: number; character: string | null }
  | { type: "cast";  lineId: number; sceneIdx: number; audioUrl: string; character: string }
  | { type: "silence"; lineId: number | null; sceneIdx: number; durationMs: number; reason: "mine-muted" | "partner-muted" | "pre-line" | "missing-cast" };

// Pad self segments so ASR-word-boundary tightness doesn't clip line
// starts (breath, soft consonants the ASR didn't catch).
const LEAD_IN_MS = 200;
const LEAD_OUT_MS = 200;
// If a "matched" line claims to span this long, the alignment is bogus
// (one line shouldn't take 8+ seconds). Treat it as missing and fall
// back to cast or silence.
const MAX_REASONABLE_SELF_MS = 8000;

function buildPlaybackQueue(
  scenes: ActorScene[],
  takes: Record<number, SceneTake | null>,
  matches: Record<number, MatchStatus>,
  cast: CastEnvelope | null,
  envelope: ActorScenesEnvelope,
  role: string,
  playSasha: boolean,
  tOthers: boolean,
  pauseBefore: "off" | "short" | "long",
): Cue[] {
  // ── FAST PATH: "Just play my recording" — no cast, no skip-mine, no
  // pre-line pauses. Single cue per scene covering its entire take.
  // No alignment is used; nothing can be clipped. Per Ryan iPhone test
  // 2026-06-26 — the default mode is "hear myself."
  //
  // endMs is set to Number.MAX_SAFE_INTEGER so the timeupdate-advance
  // check NEVER fires for these cues; we rely entirely on the audio
  // element's natural `ended` event. Browser-decoded duration is the
  // truth, not the backend metadata. Per Ryan iPhone test 2026-06-27:
  // backend reported 111s for a 129s file, causing playback to "clip"
  // 18s early.
  if (!tOthers && playSasha && pauseBefore === "off") {
    const fast: Cue[] = [];
    scenes.forEach((sc, sceneIdx) => {
      const take = takes[sc.n];
      if (!take) return;
      fast.push({
        type: "self", lineId: -1, sceneIdx,
        audioUrl: take.audio_url, startMs: 0, endMs: Number.MAX_SAFE_INTEGER,
        character: null,
      });
    });
    return fast;
  }

  const pauseMs = pauseBefore === "off" ? 0 : pauseBefore === "short" ? 1500 : 3500;
  const isActor = (lid: number) => (envelope.line_chars[lid] || "").toUpperCase() === role.toUpperCase();
  const out: Cue[] = [];
  scenes.forEach((sc, sceneIdx) => {
    const take = takes[sc.n];
    const match = matches[sc.n];
    const rawManifest = (match?.manifest ?? []).slice().sort((a, b) => a.start_ms - b.start_ms);

    // Lookup by line_id. We iterate the FULL scene line list (not the
    // manifest) so every line generates a cue even when alignment failed.
    const manifestByLine = new Map<number, SceneManifestEntry>();
    for (const m of rawManifest) manifestByLine.set(m.line_id, m);

    // Pad valid self segments; clamp to neighbor boundaries.
    const padded = new Map<number, { start_ms: number; end_ms: number }>();
    for (let i = 0; i < rawManifest.length; i++) {
      const m = rawManifest[i];
      const prev = rawManifest[i - 1];
      const next = rawManifest[i + 1];
      const minStart = prev ? prev.end_ms : 0;
      const maxEnd = next ? next.start_ms : m.end_ms + LEAD_OUT_MS;
      padded.set(m.line_id, {
        start_ms: Math.max(minStart, m.start_ms - LEAD_IN_MS),
        end_ms: Math.min(maxEnd, m.end_ms + LEAD_OUT_MS),
      });
    }

    const lineIds = sc.line_ids || [];
    for (const lid of lineIds) {
      const ch = envelope.line_chars[lid] ?? null;
      const actor = isActor(lid);
      const m = manifestByLine.get(lid);
      const slot = padded.get(lid);
      const slotDur = slot ? (slot.end_ms - slot.start_ms) : 0;
      const slotIsSane = slot && slotDur > 0 && slotDur < MAX_REASONABLE_SELF_MS;

      // Pre-line pause for actor's lines
      if (actor && pauseMs > 0) {
        out.push({ type: "silence", lineId: null, sceneIdx, durationMs: pauseMs, reason: "pre-line" });
      }

      if (actor) {
        if (playSasha) {
          if (take && slotIsSane) {
            out.push({
              type: "self", lineId: lid, sceneIdx,
              audioUrl: take.audio_url,
              startMs: slot!.start_ms, endMs: slot!.end_ms,
              character: ch,
            });
          } else {
            // No alignment / unusable alignment for this line — leave a
            // small gap so she still knows it's her turn.
            out.push({ type: "silence", lineId: lid, sceneIdx,
              durationMs: 1200, reason: "mine-muted" });
          }
        } else {
          // Mute her line; estimate duration from the manifest slot
          // when sane, else a small default gap.
          out.push({ type: "silence", lineId: lid, sceneIdx,
            durationMs: slotIsSane ? slotDur : 1200, reason: "mine-muted" });
        }
      } else {
        // Partner line
        if (!tOthers) {
          out.push({ type: "silence", lineId: lid, sceneIdx,
            durationMs: slotIsSane ? slotDur : 600, reason: "partner-muted" });
        } else {
          // PREFER CAST AUDIO over her recording for partner lines —
          // bypasses the alignment problem completely for partners.
          const castEntry = cast?.manifest?.[String(lid)];
          if (castEntry && castEntry.status === "ready") {
            out.push({ type: "cast", lineId: lid, sceneIdx,
              audioUrl: castEntry.audio_url, character: ch || "" });
          } else if (take && slotIsSane) {
            out.push({
              type: "self", lineId: lid, sceneIdx,
              audioUrl: take.audio_url,
              startMs: slot!.start_ms, endMs: slot!.end_ms,
              character: ch,
            });
          } else {
            // No cast, no sane self slot — skip silently.
            out.push({ type: "silence", lineId: lid, sceneIdx,
              durationMs: 600, reason: "missing-cast" });
          }
        }
      }
    }
  });
  return out;
}

function PlayerScreen({
  scenes, takes, matches, cast, role, playSasha, tOthers, pauseBefore, envelope, onDone, onExit, title,
}: {
  scenes: ActorScene[];
  takes: Record<number, SceneTake | null>;
  matches: Record<number, MatchStatus>;
  cast: CastEnvelope | null;
  role: string;
  playSasha: boolean;
  tOthers: boolean;
  pauseBefore: "off" | "short" | "long";
  envelope: ActorScenesEnvelope;
  onDone: () => void;
  onExit: () => void;
  title: string;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [cueIdx, setCueIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  // Distinguish user pause from iOS-imposed pause (visibility, lock screen,
  // notification interrupt, etc). We only auto-resume the latter.
  const userPausedRef = useRef(false);
  const wantsPlayingRef = useRef(false);

  // Snapshot the queue ONCE on Player mount. Per Ryan iPhone test
  // 2026-06-26: the background scene-status refresh kept rewriting the
  // takes/matches object references after Player mounted, which caused
  // useMemo to rebuild the queue and restart playback.
  const [queue] = useState(() =>
    buildPlaybackQueue(scenes, takes, matches, cast, envelope, role, playSasha, tOthers, pauseBefore),
  );
  // Track the audio element's REAL duration (iOS-decoded) for the total
  // time display, not the take metadata which can drift.
  const [audioDurationMs, setAudioDurationMs] = useState(0);

  const cueIdxRef = useRef(cueIdx);
  cueIdxRef.current = cueIdx;
  const playingRef = useRef(false);
  playingRef.current = playing;
  const silenceTimerRef = useRef<number | null>(null);
  const cueStartWallMsRef = useRef(0);

  // Total estimated time across the queue, for progress UI.
  const totalEstMs = useMemo(() => {
    let t = 0;
    for (const c of queue) {
      if (c.type === "silence") t += c.durationMs;
      else if (c.type === "self") t += (c.endMs - c.startMs);
      else if (c.type === "cast") t += 3500; // rough estimate; refined at play time
    }
    return t;
  }, [queue]);

  // Live elapsed time (sum of completed cues + current cue progress).
  // We recompute via interval rather than React state on every audio tick
  // so progress is smooth.
  const [elapsedMs, setElapsedMs] = useState(0);
  const completedSumRef = useRef(0);

  function clearSilenceTimer() {
    if (silenceTimerRef.current !== null) {
      window.clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }

  function cueDurationMs(c: Cue): number {
    if (c.type === "silence") return c.durationMs;
    if (c.type === "self") {
      // Cap at 1 hour to defend against the fast-path's MAX_SAFE_INTEGER
      // sentinel ending up in totalEstMs / progress math.
      return Math.min(c.endMs - c.startMs, 60 * 60 * 1000);
    }
    // cast — measured at play time, fallback estimate
    return 3500;
  }

  // Start a cue at the given index. The audio element handles self+cast;
  // silence is just a timeout.
  function playCueAt(idx: number) {
    clearSilenceTimer();
    cueStartWallMsRef.current = performance.now();
    if (idx >= queue.length) {
      // Done
      setPlaying(false);
      onDone();
      return;
    }
    setCueIdx(idx);
    const cue = queue[idx];
    const el = audioRef.current;
    if (!el) return;

    if (cue.type === "silence") {
      // Keep <audio> paused; schedule advance.
      try { el.pause(); } catch { /* no-op */ }
      if (!playingRef.current) return; // we're paused — wait for user to resume
      silenceTimerRef.current = window.setTimeout(() => {
        completedSumRef.current += cue.durationMs;
        playCueAt(idx + 1);
      }, cue.durationMs);
      return;
    }

    // self or cast — drive via the audio element
    if (cue.type === "self") {
      // Avoid re-loading the same URL when adjacent self cues use it
      const same = el.src === cue.audioUrl || el.currentSrc === cue.audioUrl;
      if (!same) {
        el.src = cue.audioUrl;
        el.load();
      }
      try { el.currentTime = cue.startMs / 1000; } catch { /* no-op */ }
    } else {
      // cast
      el.src = cue.audioUrl;
      el.load();
    }
    el.play().catch(() => { /* iOS may need a user gesture; user can tap play */ });
  }

  // Initialize playback on mount + when queue rebuilds
  useEffect(() => {
    completedSumRef.current = 0;
    setCueIdx(0);
    setPlaying(true);
    playingRef.current = true;
    // Defer until <audio> ref is wired
    const t = window.setTimeout(() => playCueAt(0), 50);
    return () => {
      window.clearTimeout(t);
      clearSilenceTimer();
      try { audioRef.current?.pause(); } catch { /* no-op */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue]);

  // For 'self' cues, watch timeupdate and stop when we've played past the
  // segment's end. Then advance to the next cue.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => {
      const cue = queue[cueIdxRef.current];
      if (!cue || cue.type !== "self") return;
      const ms = el.currentTime * 1000;
      if (ms >= cue.endMs) {
        completedSumRef.current += (cue.endMs - cue.startMs);
        playCueAt(cueIdxRef.current + 1);
      }
    };
    const onEnded = () => {
      const cue = queue[cueIdxRef.current];
      if (!cue) return;
      const dur = cueDurationMs(cue);
      completedSumRef.current += dur;
      playCueAt(cueIdxRef.current + 1);
    };
    const onPause = () => {
      setPlaying(false);
      // Auto-resume if iOS paused us against our will (visibility change,
      // lock screen, etc). Only if the user didn't tap pause.
      if (wantsPlayingRef.current && !userPausedRef.current && el.currentTime < el.duration - 0.5) {
        window.setTimeout(() => {
          if (wantsPlayingRef.current && !userPausedRef.current) {
            el.play().catch(() => { /* needs gesture, leave paused */ });
          }
        }, 100);
      }
    };
    const onPlay = () => setPlaying(true);
    const onLoaded = () => {
      const d = el.duration;
      if (isFinite(d) && d > 0) setAudioDurationMs(Math.round(d * 1000));
    };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", onEnded);
    el.addEventListener("pause", onPause);
    el.addEventListener("play", onPlay);
    el.addEventListener("loadedmetadata", onLoaded);
    el.addEventListener("durationchange", onLoaded);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("loadedmetadata", onLoaded);
      el.removeEventListener("durationchange", onLoaded);
    };
  }, [queue]);

  // iOS Safari pause-prevention bundle:
  //   - Wake Lock so the screen doesn't sleep while playing
  //   - Visibility listener to resume when the tab regains focus
  //   - Media Session playbackState so iOS treats us as an active session
  useEffect(() => {
    let wakeLock: any = null;
    const reqLock = async () => {
      try {
        if ('wakeLock' in navigator && wantsPlayingRef.current) {
          wakeLock = await (navigator as any).wakeLock.request('screen');
        }
      } catch { /* not supported */ }
    };
    const onVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        // Re-request lock; iOS releases it when tab loses focus.
        await reqLock();
        // Resume audio if we wanted to keep playing
        const el = audioRef.current;
        if (el && wantsPlayingRef.current && !userPausedRef.current && el.paused && el.currentTime < el.duration - 0.5) {
          el.play().catch(() => { /* gesture needed */ });
        }
      }
    };
    reqLock();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      try { wakeLock?.release(); } catch { /* no-op */ }
    };
  }, []);

  // Sync Media Session playbackState so iOS treats us as actively playing
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    try { (navigator as any).mediaSession.playbackState = playing ? "playing" : "paused"; } catch { /* no-op */ }
  }, [playing]);

  // Live elapsed tick for the progress bar
  useEffect(() => {
    const id = window.setInterval(() => {
      if (!playingRef.current) return;
      const cue = queue[cueIdxRef.current];
      let cueElapsed = 0;
      if (cue) {
        if (cue.type === "silence") {
          cueElapsed = Math.min(cue.durationMs, performance.now() - cueStartWallMsRef.current);
        } else {
          const el = audioRef.current;
          cueElapsed = el ? (cue.type === "self"
            ? Math.max(0, el.currentTime * 1000 - cue.startMs)
            : el.currentTime * 1000) : 0;
        }
      }
      setElapsedMs(completedSumRef.current + cueElapsed);
    }, 250);
    return () => window.clearInterval(id);
  }, [queue]);

  // Media Session
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const ms: any = navigator.mediaSession;
    const cue = queue[cueIdx];
    const sceneName = cue ? scenes[cue.sceneIdx]?.name : "";
    try {
      ms.metadata = new (window as any).MediaMetadata({
        title, artist: role, album: sceneName || "Run Lines",
      });
    } catch { /* no-op */ }
    const safe = (fn: () => void) => () => { try { fn(); } catch { /* no-op */ } };
    try {
      ms.setActionHandler("play", safe(() => {
        setPlaying(true); playingRef.current = true;
        // If we were in a silence, restart its timer; else resume audio
        const c = queue[cueIdxRef.current];
        if (c?.type === "silence") playCueAt(cueIdxRef.current);
        else audioRef.current?.play();
      }));
      ms.setActionHandler("pause", safe(() => {
        setPlaying(false); playingRef.current = false;
        clearSilenceTimer();
        audioRef.current?.pause();
      }));
      ms.setActionHandler("previoustrack", safe(() => stepCue(-1)));
      ms.setActionHandler("nexttrack", safe(() => stepCue(1)));
    } catch { /* no-op */ }
    return () => {
      try {
        ms.setActionHandler("play", null);
        ms.setActionHandler("pause", null);
        ms.setActionHandler("previoustrack", null);
        ms.setActionHandler("nexttrack", null);
      } catch { /* no-op */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, role, queue, cueIdx, scenes]);

  function togglePlay() {
    if (playing) {
      setPlaying(false); playingRef.current = false;
      userPausedRef.current = true;
      wantsPlayingRef.current = false;
      clearSilenceTimer();
      try { audioRef.current?.pause(); } catch { /* no-op */ }
    } else {
      setPlaying(true); playingRef.current = true;
      userPausedRef.current = false;
      wantsPlayingRef.current = true;
      const cue = queue[cueIdxRef.current];
      if (cue?.type === "silence") {
        playCueAt(cueIdxRef.current);
      } else {
        audioRef.current?.play().catch(() => { /* no-op */ });
      }
    }
  }

  function stepCue(dir: 1 | -1) {
    // Skip "pre-line" silences when stepping so a tap on prev doesn't land
    // in a pause and feel like nothing happened.
    let target = cueIdxRef.current + dir;
    while (target >= 0 && target < queue.length && queue[target].type === "silence" && (queue[target] as any).reason === "pre-line") {
      target += dir;
    }
    if (target < 0) target = 0;
    if (target >= queue.length) target = queue.length - 1;
    completedSumRef.current = queue.slice(0, target).reduce((a, c) => a + cueDurationMs(c), 0);
    playCueAt(target);
  }

  const currentCue = queue[cueIdx];
  const currentSceneIdx = currentCue?.sceneIdx ?? 0;
  const currentScene = scenes[currentSceneIdx];
  const speakerLabel = (() => {
    if (!playing && elapsedMs === 0) return "Ready";
    if (!playing) return "Paused";
    if (!currentCue) return "…";
    if (currentCue.type === "silence") {
      if (currentCue.reason === "pre-line") return "Get ready — your line";
      if (currentCue.reason === "mine-muted") return "Your line — speak now";
      if (currentCue.reason === "partner-muted") return "(partner line — muted)";
      return "…";
    }
    if (currentCue.type === "self") {
      const ch = currentCue.character;
      if (ch && ch.toUpperCase() === role.toUpperCase()) return "Your line";
      return ch ? `${ch} speaking · your voice` : "Scene description";
    }
    // cast
    return `${currentCue.character} speaking`;
  })();

  const elapsedSec = Math.floor(elapsedMs / 1000);
  // Prefer the browser-decoded audio duration (truth) over our estimate
  // (which was just summed from queue cue durations and can drift).
  const totalSec = Math.max(
    1,
    Math.floor(audioDurationMs > 0 ? audioDurationMs / 1000 : totalEstMs / 1000),
  );

  return (
    <ScreenShell>
      <div style={{ padding: "12px 22px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Label>RUN · {currentScene?.label}</Label>
        <button onClick={onExit} style={{ appearance: "none", border: "none", background: "none", cursor: "pointer", color: TOKENS.mutedPlum, font: `500 13px ${TOKENS.font}` }}>✕</button>
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 28px", textAlign: "center", gap: 22 }}>
        <div style={{ font: `300 28px ${TOKENS.font}`, color: TOKENS.deepInk }}>{title}</div>
        <div style={{ font: `400 13.5px ${TOKENS.font}`, color: TOKENS.mutedPlum }}>
          Scene {currentSceneIdx + 1} of {scenes.length} · {currentScene?.name}
        </div>

        {/* Speaker label — the calm focus */}
        <div style={{ marginTop: 6, padding: "26px 22px", borderRadius: 28, background: TOKENS.glassThin, backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", border: `1px solid ${TOKENS.glassStrong}`, minWidth: 240 }}>
          <Label>NOW</Label>
          <div style={{ font: `300 22px ${TOKENS.font}`, color: TOKENS.deepInk, marginTop: 8 }}>
            {speakerLabel}
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ width: "100%", maxWidth: 320, marginTop: 6 }}>
          <div style={{ height: 4, borderRadius: 999, background: "rgba(32,32,42,0.15)", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${Math.min(100, (elapsedSec / totalSec) * 100)}%`, background: PRIMARY_GRADIENT, transition: "width .2s" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", font: `500 11px ${TOKENS.font}`, color: TOKENS.mutedPlum, marginTop: 8, fontVariantNumeric: "tabular-nums" }}>
            <span>{fmtTime(elapsedSec)}</span>
            <span>{fmtTime(totalSec)}</span>
          </div>
        </div>
      </div>

      <div style={{ padding: "0 22px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Transport — line-level back/forward */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 28 }}>
          <button onClick={() => stepCue(-1)} aria-label="Previous line" style={{ appearance: "none", border: "none", background: "none", cursor: "pointer", width: 50, height: 50, color: TOKENS.deepInk, font: `400 24px ${TOKENS.font}` }}>‹‹</button>
          <button onClick={togglePlay} style={{ appearance: "none", border: "none", cursor: "pointer", width: 68, height: 68, borderRadius: "50%", background: PRIMARY_GRADIENT, color: "#fff", font: `400 26px ${TOKENS.font}`, boxShadow: "0 12px 26px rgba(242,95,87,.32)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {playing ? "❚❚" : "▶"}
          </button>
          <button onClick={() => stepCue(1)} aria-label="Next line" style={{ appearance: "none", border: "none", background: "none", cursor: "pointer", width: 50, height: 50, color: TOKENS.deepInk, font: `400 24px ${TOKENS.font}` }}>››</button>
        </div>
      </div>

      <audio ref={audioRef} preload="auto" playsInline />
    </ScreenShell>
  );
}

function DoneScreen({ sceneCount, playSasha, tDesc, onRunAgain, onChangeScenes, onEditSetup, onExit }: {
  sceneCount: number; playSasha: boolean; tDesc: boolean;
  onRunAgain: () => void; onChangeScenes: () => void; onEditSetup: () => void; onExit: () => void;
}) {
  return (
    <ScreenShell>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 32px", textAlign: "center", gap: 22 }}>
        <div style={{ width: 96, height: 96, borderRadius: "50%", background: PRIMARY_GRADIENT, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 14px 32px rgba(242,95,87,.4)" }}>
          <CheckIcon size={44} />
        </div>
        <ScreenTitle>Run complete</ScreenTitle>
        <div style={{ font: `400 14px ${TOKENS.font}`, color: "#3a3543", opacity: 0.85, lineHeight: 1.5, maxWidth: 280 }}>
          {sceneCount} scene{sceneCount === 1 ? "" : "s"} · your lines {playSasha ? "playing" : "muted"} · descriptions {tDesc ? "on" : "off"}.
        </div>
      </div>
      <div style={{ padding: "0 22px 22px", display: "flex", flexDirection: "column", gap: 10 }}>
        <PrimaryButton onClick={onRunAgain}>Run Again</PrimaryButton>
        <SecondaryButton onClick={onChangeScenes}>Change Scenes</SecondaryButton>
        <SecondaryButton onClick={onEditSetup}>Edit Playback</SecondaryButton>
        <SecondaryButton onClick={onExit}>Back to Booth</SecondaryButton>
      </div>
    </ScreenShell>
  );
}
